// Display helpers ported from CONTROL V4 (wgMoney, wgHrs, wgKg, dmy). They
// format only; no wage figure is derived here.
import { addDaysLocal, parseIsoLocal, todayLocal } from '../timesheets/time'
import type { PayPeriod, Scope, WageGroup } from './types'

const MON3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function money(v: number | null | undefined): string {
  const n = Number(v) || 0
  return (n < 0 ? '−$' : '$') + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const hrs = (v: number | null | undefined) => (Math.round((Number(v) || 0) * 100) / 100).toFixed(2) + ' h'

export const kg = (v: number | null | undefined) => (!v ? '—' : (Math.round(Number(v) * 10) / 10).toFixed(1) + ' kg')

export function dmy(iso: string | null | undefined): string {
  if (!iso) return '—'
  const p = iso.slice(0, 10).split('-')
  return Number(p[2]) + ' ' + MON3[Number(p[1]) - 1] + ' ' + p[0]
}

export const rangeLabel = (from: string, to: string) => (from === to ? dmy(from) : dmy(from) + ' – ' + dmy(to))

// "YYYY-MM-DDTHH:MM" → "HH:MM", with the date when it is not `base`.
export function clock(label: string | null | undefined, base?: string | null): string {
  if (!label) return '—'
  const t = label.slice(11, 16)
  return base && label.slice(0, 10) !== base ? dmy(label.slice(0, 10)) + ' ' + t : t
}

export function daysBetween(from: string, to: string): string[] {
  const out: string[] = []
  for (let d = from; d <= to && out.length < 400; d = addDaysLocal(d, 1)) out.push(d)
  return out
}

// Monday-first weeks, relative to today (V4's presets are fixed demo dates).
export function mondayOf(iso: string): string {
  const dow = (parseIsoLocal(iso).getDay() + 6) % 7
  return addDaysLocal(iso, -dow)
}

export const WEEKLY_MONDAY: PayPeriod = { weeks: 1, start_dow: 1, anchor: null }

const dayNo = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, d) / 86400000
}

// The pay period holding `date` (h 3484-3485; the API's lib/payPeriod.js):
// work dates, no zone.
export function payPeriodOf(date: string, pp: PayPeriod = WEEKLY_MONDAY) {
  const len = pp.weeks === 2 ? 14 : 7
  const isoDow = ((parseIsoLocal(date).getDay() + 6) % 7) + 1
  const back = pp.weeks === 2 && pp.anchor ? (((dayNo(date) - dayNo(pp.anchor)) % 14) + 14) % 14 : (isoDow - pp.start_dow + 7) % 7
  const from = addDaysLocal(date, -back)
  return { from, to: addDaysLocal(from, len - 1) }
}

// The presets (h 3123: this week, week to date, last week, today), in the
// tenant's pay period and relative to the tenant's today.
export function presets(today = todayLocal(), pp: PayPeriod = WEEKLY_MONDAY) {
  const cur = payPeriodOf(today, pp)
  const len = pp.weeks === 2 ? 14 : 7
  const unit = pp.weeks === 2 ? 'fortnight' : 'week'
  const Unit = unit[0].toUpperCase() + unit.slice(1)
  return [
    { label: `This ${unit}`, from: cur.from, to: cur.to },
    { label: `${Unit} to date`, from: cur.from, to: today },
    { label: `Last ${unit}`, from: addDaysLocal(cur.from, -len), to: addDaysLocal(cur.from, -1) },
    { label: 'Today', from: today, to: today },
  ]
}

export const DOW_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// "Weekly, Monday to Sunday" / "Fortnightly from Monday 31 Aug 2026".
export function payPeriodText(pp: PayPeriod = WEEKLY_MONDAY) {
  const start = DOW_NAMES[pp.start_dow - 1]
  if (pp.weeks === 2) return `Fortnightly, ${start} to ${DOW_NAMES[(pp.start_dow + 5) % 7]}` + (pp.anchor ? `, one starting ${dmy(pp.anchor)}` : '')
  return `Weekly, ${start} to ${DOW_NAMES[(pp.start_dow + 5) % 7]}`
}

export const SCOPE_LABEL: Record<Scope, string> = { daily: 'Daily', team: 'By Team', combined: 'Combined' }

export const SCOPE_HELP: Record<Scope, string> = {
  daily: 'Minimum wage and top-ups are assessed separately for each worker, team and day.',
  team: 'Matching team sessions are combined across the selected period before minimum wage and top-ups are assessed.',
  combined: 'All qualifying work for each worker is combined across the selected period before minimum wage and top-ups are assessed.',
}

export const BLOCKED_LABEL = 'Allocation undetermined — see admin'
export const NO_MIN_LABEL = 'No minimum rate — see admin'

const undetermined = (g: WageGroup) => g.blocked_reasons.some((r) => r.code === 'UNDETERMINED_ALLOCATION')

// What a blocked row shows in place of its figures (plan §2b: never $0.00,
// never omitted).
export const blockedLabel = (g: WageGroup) => (undetermined(g) ? BLOCKED_LABEL : NO_MIN_LABEL)

// The one state column, worst first (V4 wgRows). A blocked row has no figure.
export function stateOf(g: WageGroup): { text: string; title: string; tone: string } {
  if (g.blocked) {
    return {
      text: undetermined(g) ? 'Undetermined' : 'No min rate',
      title: g.blocked_reasons.map((r) => dmy(r.work_date) + ': ' + r.message).join('\n'),
      tone: '#8f2f1c',
    }
  }
  if (g.overlap)
    return {
      text: 'Overlap',
      title: 'Two sessions claim the same minutes on this day, so this figure is provisional. Open the worker in Timesheets to resolve it.',
      tone: '#8f2f1c',
    }
  if (g.no_rate)
    return {
      text: 'No piece rate',
      title: 'Picking here had no priced job in force, so it was paid at the hourly or minimum rate. Set the rate on the picking job to price it.',
      tone: '#8c491a',
    }
  if (g.stale) return { text: 'Recalculating', title: 'Queued for recalculation. This figure comes from the last valid basis.', tone: '#7a5410' }
  return { text: '', title: '', tone: '' }
}

export const plural = (n: number, one: string, many: string) => n + ' ' + (n === 1 ? one : many)
